import React from "react";
import classNames from "classnames";
import {
  useTable,
  useGlobalFilter,
  useFilters,
  usePagination,
  useSortBy,
  HeaderGroup,
} from "react-table";
import { useRouter } from "next/router";

import styles from "./AscTable.module.scss";
import AscAdminPagination from "../AscAdminPagination/AscAdminPagination";
import { AdminChevronDownIcon } from "../AscIcons";

export interface AscTableProps {
  className?: string;
  myTable?: string;
  tHead?: string;
  tHeadRow?: string;
  tH?: string;
  theadCol?: string;
  tBody?: string;
  tRow?: string;
  rowBody?: string;
  pureCellFlow?: string;
  tD?: string;
  tBodyValue?: string;
  tableColumns: any[];
  tableData: any[];
  showHeader?: boolean;
  showPagination?: boolean;
  paginationStyle?: string;
  eachPageSize?: number;
  totalPageCount?: number;
  currentPage?: number;
  onClickTD?: () => void;
  triggerNextPage?: (pageIndex: number) => void;
  triggerPrevPage?: (pageIndex: number) => void;
}

const AscTable = ({
  className,
  myTable,
  tHead,
  tHeadRow,
  tH,
  theadCol,
  tBody,
  rowBody,
  tRow,
  pureCellFlow,
  tD,
  tBodyValue,
  tableColumns,
  tableData,
  showHeader,
  showPagination = true,
  paginationStyle,
  eachPageSize = 7,
  totalPageCount,
  currentPage,
  onClickTD,
  triggerNextPage,
  triggerPrevPage,
}: AscTableProps) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    nextPage,
    previousPage,
    pageCount,
    state: { pageIndex },
  } = useTable(
    {
      columns: tableColumns,
      data: tableData,
      initialState: { pageSize: eachPageSize },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const handleNextPage = () => {
    if (totalPageCount) {
      triggerNextPage?.(currentPage ? currentPage : pageIndex);
    } else {
      nextPage();
    }
  };

  const handlePreviousPage = () => {
    if (totalPageCount) {
      triggerPrevPage?.(currentPage ? currentPage : pageIndex);
    } else {
      previousPage();
    }
  };

  return (
    <div className={classNames(styles.ground, className)}>
      <div className={classNames(styles.myTable, myTable)} {...getTableProps()}>
        {showHeader && (
          <div className={classNames(styles.tHead, tHead)}>
            {headerGroups.map((headerGroup) => (
              <div
                className={classNames(styles.tHeadRow, tHeadRow)}
                {...headerGroup.getHeaderGroupProps()}
              >
                {headerGroup.headers.map(
                  (column: HeaderGroup & { enableSorting?: boolean }) => {
                    return (
                      <div
                        className={classNames(styles.header, theadCol)}
                        key={column.id}
                      >
                        <div
                          className={classNames(styles.tH, tH)}
                          {...column.getHeaderProps()}
                        >
                          {column.render("Header")}
                        </div>
                        {column?.enableSorting && (
                          <ChevronIcon id={column.id} />
                        )}
                      </div>
                    );
                  }
                )}
              </div>
            ))}
          </div>
        )}

        <div
          className={classNames(styles.tBody, tBody)}
          {...getTableBodyProps()}
        >
          {rows.map((row) => {
            prepareRow(row);
            return (
              <div
                className={classNames(styles.tRow, tRow)}
                {...row.getRowProps()}
              >
                <div className={classNames(styles.rowBody, rowBody)}>
                  <div
                    onClick={onClickTD}
                    className={classNames(styles.pureCellFlow, pureCellFlow)}
                  >
                    {row.cells.map((cell) => {
                      return (
                        <div
                          className={classNames(styles.tD, tD)}
                          data-label={cell.column.id}
                          {...cell.getCellProps()}
                        >
                          <div className={classNames(styles.value, tBodyValue)}>
                            {cell.render("Cell")}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showPagination === true && (
        <div className={classNames(styles.pagination, paginationStyle)}>
          <AscAdminPagination
            goNext={handleNextPage}
            goBack={handlePreviousPage}
            pageCount={totalPageCount ? totalPageCount : pageCount}
          />
        </div>
      )}
    </div>
  );
};

export default AscTable;

const ChevronIcon = ({ id }: { id: string }) => {
  const router = useRouter();
  const sortBy = router.query.sortBy as string;
  const sortOrder = router.query.sortOrder as string;
  const [iconPosition, setIconPosition] = React.useState(false);

  const handleClick = (str: string) => {
    setIconPosition(!iconPosition);
    router.push(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          page: 1,
          sortBy: id,
          sortOrder: str,
        },
      },
      undefined,
      { shallow: true }
    );
  };

  return (
    <div className={styles.iconContainer}>
      {(sortBy === id && sortOrder === "asc") || iconPosition ? (
        <AdminChevronDownIcon
          onClick={() => handleClick("desc")}
          className={classNames(styles.icon, styles.iconRotate)}
        />
      ) : (
        <AdminChevronDownIcon
          onClick={() => handleClick("asc")}
          className={classNames(styles.icon)}
        />
      )}
    </div>
  );
};
